import { useCallback, useLayoutEffect, useMemo } from "react";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  addAllToCartThunk,
  createWishlistThunk,
  selectWishlistThunk,
  setEntryUIState,
  setSelectedEntryIds,
  updateWishlistCommentThunk,
  wishlistPageMounted,
} from "~/features/wishlist/redux/wishlistSlice";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import Spinner from "~/common/components/Spinner/Spinner";
import {
  getSelectPriceParamsFromWishlistEntry,
  selectFullWishlist,
  selectIsSelectedWishlistProcessed,
  selectIsWishlistsError,
  selectIsWishlistsLoading,
  selectSelectedWishlist,
  selectSelectedWishlistId,
  selectShowATCNotReadyMsg,
  selectShowWSErrorMsg,
  selectWishlists,
} from "~/features/wishlist/redux/wishlistSelectors";

import { styled } from "~/common/mui";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { userIsCustomer, userIsRegistered } from "~/domains/user/utils/roles";
import { AddToCartButton, RequestOfferButton } from "~/pages/WishlistPage/components/buttons/WishlistButtons";
import { NoWishlistContent } from "~/pages/WishlistPage/components/NoWishlistContent";
import WishlistDropdown from "~/features/wishlist/components/WishlistDropdown";
import EditWishlistNamePopup from "~/features/wishlist/components/modals/EditWishlistNamePopup";
import { WishlistDetails } from "~/pages/WishlistPage/components/WishlistDetails/WishlistDetails";
import RenameWishlistButton from "~/pages/WishlistPage/components/buttons/RenameWishlistButton";
import {
  getCoreRowModel,
  getExpandedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { type ExpandedResolvedWishlistEntryModel } from "~/features/wishlist/types/model";
import { DeleteWishlistButton } from "~/pages/WishlistPage/components/buttons/DeleteWishlistButton";
import { ShareWishlistButton } from "~/pages/WishlistPage/components/buttons/ShareWishlistButton";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/pages/WishlistPage/components/buttons/CommentButton";
import { useTranslation } from "react-i18next";
import { getTariff, selectNetPrice } from "~/domains/product/utils/productDescriptorUtils";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { canSeeAddToCartButton } from "~/utils/features";
import { Alert } from "~/common/mui/Alert/Alert";

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  [theme.breakpoints.up("desktop-s")]: {
    display: "grid",
    gridTemplateAreas: `
      "wishlist actions"
      "comment comment"
      "messages messages"
      "details details"
      "totals totals"
      "surcharge cta"
    `,
    gridTemplateColumns: "1fr auto",
  },
}));

const WishlistSelect = styled("div")(({ theme }) => ({
  gridArea: "wishlist",
  [theme.breakpoints.up("tablet")]: {
    width: "50%",
  },
  [theme.breakpoints.up("desktop-s")]: {
    width: "66.666%",
  },
  [theme.breakpoints.up("desktop-l")]: {
    width: "50%",
  },
}));

const Actions = styled("div")(({ theme }) => ({
  gridArea: "actions",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(4),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up("tablet")]: {
    marginTop: theme.spacing(4),
    display: "flex",
    flexFlow: "row",
  },
  [`@media (min-width: ${theme.breakpoints.values.tablet}px) and (max-width: ${theme.breakpoints.values["desktop-s"] - 1}px)`]: {
    "& > *:last-child": {
      marginLeft: "auto",
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    justifyContent: "flex-end",
    margin: 0,
    alignSelf: "center",
  },
}));

const StyledAddToCartButton = styled(AddToCartButton)(({ theme }) => ({
  width: "100%",
  gridColumn: "1 / -1",
  [theme.breakpoints.up("tablet")]: {
    width: "auto",
  },
}));

const StyledRequestOfferButton = styled(RequestOfferButton)(({ theme }) => ({
  width: "100%",
  gridColumn: "1 / -1",
  [theme.breakpoints.up("tablet")]: {
    width: "auto",
  },
}));

const MessagesArea = styled("div")(({ theme }) => ({
  gridArea: "messages",
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  "&:not(:empty)": {
    marginBottom: theme.spacing(4),
  },
}));

const Details = styled("div")({
  gridArea: "details",
});

const CommentBlock = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(8),
  marginTop: theme.spacing(2),
}));

const CommentBlockTitle = styled("div")(({ theme }) => ({
  gridArea: "comment",
  marginBottom: theme.spacing(2),
  "& > span": {
    color: theme.palette.text.secondary,
  },
}));

const LowerCta = styled("div")(({ theme }) => ({
  gridArea: "cta",
  justifySelf: "flex-end",
  alignSelf: "inherit",
  marginTop: theme.spacing(8),
  [theme.breakpoints.up("tablet")]: {
    alignSelf: "flex-end",
  },
}));

const Surcharge = styled("div")(({ theme }) => ({
  gridArea: "surcharge",
  marginTop: 30,
  [theme.breakpoints.up("desktop-s")]: {
    alignSelf: "flex-end",
  },
}));

const Summary = styled("div")(({ theme }) => ({
  gridArea: "totals",
  marginTop: theme.spacing(6),
  [theme.breakpoints.up("tablet")]: {
    width: "66.666%",
    alignSelf: "flex-end",
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginTop: theme.spacing(8),
    width: "33.333%",
    justifySelf: "flex-end",
  },
}));

interface WishlistPageStatusViewProps {
  state: "loading" | "error" | "noWishlists";
}

export const WishlistPageStatusView = ({ state }: WishlistPageStatusViewProps) => {
  const { t } = useTranslation();

  return (
    <>
      <PageTitle>{t("wishlistPage.title")}</PageTitle>
      {state === "loading" && <Spinner />}
      {state === "error" && <Alert severity="error">{t("generic.error.ajax")}</Alert>}
      {state === "noWishlists" && <NoWishlistContent onCreateWishlist={() => {}} />}
    </>
  );
};

export const WishlistPage = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  useOnMount(() => {
    dispatch(wishlistPageMounted());
  });

  const isLoading = useSelector(selectIsWishlistsLoading);
  const isError = useSelector(selectIsWishlistsError);
  const wishlists = useSelector(selectWishlists);

  const hasWishlists = wishlists.length > 0;
  const canPurchase = canSeeAddToCartButton();
  const isRegistered = userIsRegistered();
  const isCustomer = userIsCustomer();

  const [createWishlistModal, openCreateWishlistModal] = useCreateWishlistPopup();

  const selectedWishlistId = useSelector(selectSelectedWishlistId);
  const selectedWishlist = useSelector(selectSelectedWishlist);

  const isProcessed = useSelector(selectIsSelectedWishlistProcessed);
  const selectedWishlistIsLoading = selectedWishlist && !isProcessed;

  const showWSErrorMsg = useSelector(selectShowWSErrorMsg);
  const showATCNotReadyMsg = useSelector(selectShowATCNotReadyMsg);

  const resolvedWishlist = useSelector((state: RootState) =>
    selectedWishlistId ? selectFullWishlist(state, selectedWishlistId) : null,
  );
  const entries = resolvedWishlist?.entries ?? [];

  const selectedEntryIds = useSelector((state: RootState) => state.wishlistUI.selectedEntryIds);

  const makeSetSaleEnabled = (index: number) => (isSaleEnabled: boolean) => {
    const entry = entries[index];
    if (entry) {
      dispatch(setEntryUIState({ entryId: entry.id, isSaleEnabled }));
    }
  };

  const { onSelectWishlist, onSaveComment } = useMemo(() => {
    return {
      onSelectWishlist: (wishlistId: string | undefined) => dispatch(selectWishlistThunk(wishlistId!)),
      onSaveComment: (comment: string) => {
        if (selectedWishlistId) {
          dispatch(updateWishlistCommentThunk(selectedWishlistId, comment));
        }
      },
    };
  }, [dispatch, selectedWishlistId]);

  const onAddSelected = () => {
    dispatch(addAllToCartThunk());
  };

  const hasAnySurcharge = useSelector((state: RootState) => {
    for (const entry of entries) {
      const priceParams = getSelectPriceParamsFromWishlistEntry(entry, entry.packCode);
      const netPrice = selectNetPrice(priceParams)(state);
      if ((netPrice?.surcharge ?? 0) > 0) {
        return true;
      }
    }
    return false;
  });

  const hasAnyTariffSurcharge = useMemo(() => {
    for (const entry of entries) {
      if (getTariff(entry.product)) {
        return true;
      }
    }
    return false;
  }, [entries]);

  const rowSelection = useMemo(() => {
    return selectedEntryIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
  }, [selectedEntryIds]);

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updaterOrValue) => {
      const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(rowSelection) : updaterOrValue;
      dispatch(setSelectedEntryIds(Object.keys(newSelection).filter((id) => !!newSelection[id])));
    },
    [dispatch, rowSelection],
  );

  const tableRef = useReactTable<ExpandedResolvedWishlistEntryModel>({
    data: entries,
    columns: [{ id: "" }],
    state: {
      rowSelection,
    },
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (data) => data.id,
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: (row) => row.original.isSelectable,
    getRowCanExpand: () => true,
  });

  let ctaButton = null;
  if (!!selectedWishlistId && canPurchase) {
    ctaButton = (
      <StyledAddToCartButton onClick={onAddSelected} disabled={selectedEntryIds.length === 0} />
    );
  } else if (isRegistered && canSeeSalesFinder()) {
    ctaButton = (
      <StyledRequestOfferButton
        wishlistId={selectedWishlistId ?? ""}
        selectedEntryIds={selectedEntryIds}
      />
    );
  }

  const { val: commentVisible, toggle: toggleCommentVisibility, setFalse: hideComment } = useBooleanState(false);
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentVisibility);

  useLayoutEffect(() => {
    hideComment();
  }, [selectedWishlistId]);

  const wishlistSelect = (
    <WishlistDropdown
      wishlists={wishlists}
      selectedWishlist={selectedWishlistId ?? undefined}
      onNewWishlist={openCreateWishlistModal}
      onWishlistSelect={onSelectWishlist}
    />
  );

  return (
    <>
      {createWishlistModal}
      <PageTitle>{t("wishlistPage.title")}</PageTitle>

      {isLoading && <Spinner />}
      {!isLoading && isError && <Alert severity="error">{t("generic.error.ajax")}</Alert>}
      {!isLoading && !isError && !hasWishlists && <NoWishlistContent onCreateWishlist={openCreateWishlistModal} />}
      {!isLoading && !isError && hasWishlists && (
        <Content>
          <WishlistSelect>{wishlistSelect}</WishlistSelect>

          {selectedWishlist ? (
            <>
              <Actions>
                <CommentButton
                  onClick={toggleComment}
                  isOpen={commentVisible}
                  hasComment={!!selectedWishlist.comment}
                />
                <ShareWishlistButton wishlist={selectedWishlist} />
                <RenameWishlistButton wishlist={selectedWishlist} />
                <DeleteWishlistButton wishlist={selectedWishlist} />
                {ctaButton}
              </Actions>

              {commentVisible && (
                <CommentBlock>
                  <CommentBlockTitle>{t("wishlistPage.comment.title")}</CommentBlockTitle>
                  <CommentInput
                    value={selectedWishlist.comment}
                    onChange={onSaveComment}
                    ref={commentRef}
                    label={t("wishlistPage.comment.label")}
                  />
                </CommentBlock>
              )}

              <MessagesArea>
                {showATCNotReadyMsg && <Alert severity={"info"}>{t("wishlistPage.fetchingInProgress")}</Alert>}
                {showWSErrorMsg && <Alert severity={"info"}>{t("wishlistPage.wsError")}</Alert>}
              </MessagesArea>

              {selectedWishlistIsLoading && <Spinner />}
              {!selectedWishlistIsLoading && (
                <Details>
                  <WishlistDetails
                    isCustomer={isCustomer}
                    tableRef={tableRef}
                    makeSetSaleEnabled={makeSetSaleEnabled}
                  />
                </Details>
              )}
            </>
          ) : (
            <Spinner />
          )}

          <Surcharge>
            <IncludesSurchargeDisclaimer
              hasSurcharge={hasAnySurcharge}
              tariffSurcharge={hasAnyTariffSurcharge}
            />
          </Surcharge>
        </Content>
      )}
    </>
  );
};

const useCreateWishlistPopup = () => {
  const { val: isModalOpen, setTrue, setFalse } = useBooleanState(false);

  const dispatch = useDispatch();
  const createWishlistCb = useCallback(
    async (name: string) => {
      setFalse();
      await dispatch(createWishlistThunk(name));
      // Re-fetching or selecting newly created wishlist will be handled by RTK mutation result if needed
      // or we can just rely on the user selecting it.
      // But legacy code tried to select it.
    },
    [dispatch, setFalse],
  );

  const modal = isModalOpen && <EditWishlistNamePopup onCancel={setFalse} onOk={createWishlistCb} />;
  return [modal, setTrue] as const;
};

export function createWishlistPage() {
  return <WishlistPage />;
}
